import { Link } from 'react-router-dom';
import {
  BookOpen,
  Building2,
  Smartphone,
  ExternalLink,
  ArrowRight
} from 'lucide-react';

const About = () => {
  const books = [
    {
      title: 'Modelové testy k přijímacím zkouškám',
      subtitle: '6 testů z fyziky, chemie a biologie',
      url: 'https://www.nakladatelstviolomouc.store/modelove-testyk--prijimacim-zkouskam-na-lekarskou-fakultu-upol-6-testu-z--fyziky--chemie-a-biologie/',
      image: '/images/books/modelovetesty.webp'
    },
    {
      title: 'Obecná biologie',
      subtitle: 'Kompletní přehled pro přípravu na přijímačky',
      url: 'https://www.nakladatelstviolomouc.store/obecna-biologie/',
      image: '/images/books/obecna-biologie.jpg'
    },
    {
      title: 'Somatologie',
      subtitle: 'Anatomie a fyziologie člověka',
      url: 'https://www.nakladatelstviolomouc.store/somatologie-anatomie-a-fyziologie-cloveka/',
      image: '/images/books/somka2.png'
    }
  ];

  const appStoreUrl = 'https://apps.apple.com/cz/app/synapso/id6746411740?l=cs';
  const googlePlayUrl = 'https://play.google.com/store/apps/details?id=com.nakladatelstviolomouc.synapso&pcampaignid=web_share';

  return (
    <div className="min-h-screen bg-dark-950 pt-24 pb-16">
      {/* Header */}
      <section className="relative pb-16">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-500/20 rounded-full blur-3xl"></div>
          <div className="absolute top-20 -left-40 w-80 h-80 bg-purple-500/20 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            O nás
          </h1>
          <p className="text-xl text-dark-300">
            Synapso vzniklo pod křídly Nakladatelství Olomouc – jednoho z nejdéle působících vzdělávacích vydavatelství v České republice.
          </p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">

        {/* O Nakladatelství */}
        <section>
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center">
              <Building2 size={24} className="text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Nakladatelství Olomouc</h2>
              <p className="text-dark-400">Více než 30 let ve vzdělávání</p>
            </div>
          </div>

          <div className="glass rounded-2xl p-6 md:p-8">
            <p className="text-dark-300 text-lg mb-6">
              Již více než 30 let vytváříme učebnice a studijní materiály, které pomáhají studentům na cestě za vzděláním. Za tu dobu jsme vydali přes 300 titulů a vytiskli více než 3 miliony učebnic pro základní a střední školy.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="glass rounded-xl p-6 text-center">
                <p className="text-4xl font-bold text-primary-400 mb-2">30+</p>
                <p className="text-dark-400">let zkušeností</p>
              </div>
              <div className="glass rounded-xl p-6 text-center">
                <p className="text-4xl font-bold text-primary-400 mb-2">300+</p>
                <p className="text-dark-400">vydaných titulů</p>
              </div>
              <div className="glass rounded-xl p-6 text-center">
                <p className="text-4xl font-bold text-primary-400 mb-2">3M+</p>
                <p className="text-dark-400">vytištěných knih</p>
              </div>
            </div>

            <p className="text-dark-300">
              Synapso představuje naši vizi moderního vzdělávání – online platforma, která kombinuje prověřenou kvalitu našich materiálů s interaktivním přístupem k učení.
            </p>

            <div className="mt-6">
              <a
                href="https://naklol.net"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl glass text-white hover:bg-dark-700 transition-colors"
              >
                Navštívit stránky nakladatelství
              </a>
            </div>
          </div>
        </section>

        {/* Učebnice */}
        <section>
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <BookOpen size={24} className="text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Naše učebnice</h2>
              <p className="text-dark-400">Testové otázky vycházejí z těchto titulů</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
            {books.map((book, index) => (
              <a
                key={index}
                href={book.url}
                target="_blank"
                rel="noopener noreferrer"
                className="glass rounded-xl overflow-hidden hover:bg-dark-800/50 transition-all hover:scale-[1.02] group"
              >
                {/* Book Image */}
                <div className="h-48 bg-dark-800 relative overflow-hidden flex items-center justify-center p-3">
                  <img
                    src={book.image}
                    alt={book.title}
                    className="max-h-full max-w-full object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = `
                        <div class="w-full h-full flex items-center justify-center bg-gradient-to-br from-dark-700 to-dark-800">
                          <svg class="w-12 h-12 text-dark-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
                          </svg>
                        </div>
                      `;
                    }}
                  />
                </div>

                {/* Book Info */}
                <div className="p-3 flex flex-col h-[120px]">
                  <h3 className="text-white font-medium text-sm mb-1 group-hover:text-primary-400 transition-colors line-clamp-2">
                    {book.title}
                  </h3>
                  <p className="text-dark-400 text-xs mb-auto line-clamp-1">{book.subtitle}</p>
                  <span className="mt-2 w-full inline-flex items-center justify-center px-3 py-2 rounded-lg gradient-bg text-white text-xs font-semibold group-hover:opacity-90 transition-all">
                    Koupit v e-shopu
                  </span>
                </div>
              </a>
            ))}
          </div>

          <div className="mt-6 text-center">
            <a
              href="https://www.nakladatelstviolomouc.store"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl glass text-white hover:bg-dark-700 transition-colors"
            >
              Zobrazit všechny tituly
            </a>
          </div>
        </section>

        {/* Mobilní aplikace */}
        <section>
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
              <Smartphone size={24} className="text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Mobilní aplikace</h2>
              <p className="text-dark-400">Synapso v kapse – uč se kdykoliv a kdekoliv</p>
            </div>
          </div>

          <div className="glass rounded-2xl p-6 md:p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              {/* Text */}
              <div>
                <p className="text-dark-300 text-lg mb-6">
                  Synapso je dostupné jako nativní aplikace pro iOS i Android. Stáhni si aplikaci a uč se v tramvaji, ve frontě nebo kdykoliv máš volnou chvilku.
                </p>

                <ul className="space-y-3 mb-8">
                  <li className="flex items-center gap-3 text-dark-300">
                    <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                    </div>
                    Offline režim pro učení bez internetu
                  </li>
                  <li className="flex items-center gap-3 text-dark-300">
                    <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                    </div>
                    Push notifikace pro připomenutí učení
                  </li>
                </ul>

                {/* Store Buttons */}
                <div className="flex flex-wrap gap-4">
                  {/* App Store Button */}
                  <a
                    href={appStoreUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 px-5 py-3 rounded-xl bg-dark-800 hover:bg-dark-700 transition-colors border border-dark-700"
                  >
                    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.09997 22C7.78997 22.05 6.79997 20.68 5.95997 19.47C4.24997 17 2.93997 12.45 4.69997 9.39C5.56997 7.87 7.12997 6.91 8.81997 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z" fill="white"/>
                    </svg>
                    <div className="text-left">
                      <p className="text-dark-400 text-xs">Stáhnout na</p>
                      <p className="text-white font-semibold">App Store</p>
                    </div>
                  </a>

                  {/* Google Play Button */}
                  <a
                    href={googlePlayUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 px-5 py-3 rounded-xl bg-dark-800 hover:bg-dark-700 transition-colors border border-dark-700"
                  >
                    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3.60945 1.30615C3.22153 1.7011 3 2.30441 3 3.09146V20.9085C3 21.6956 3.22153 22.2989 3.60945 22.6939L3.68561 22.7631L13.8437 12.605V12.395L3.68561 2.23692L3.60945 2.30615V1.30615Z" fill="#4285F4"/>
                      <path d="M17.2356 16.0014L13.8437 12.6095V12.3905L17.2356 8.99854L17.3331 9.05479L21.3843 11.377C22.5386 12.0403 22.5386 13.1597 21.3843 13.823L17.3331 16.1452L17.2356 16.0014Z" fill="#FBBC04"/>
                      <path d="M17.3331 16.1452L13.8437 12.5L3.60945 22.6939C4.01702 23.1192 4.68169 23.1673 5.43146 22.7428L17.3331 16.1452Z" fill="#EA4335"/>
                      <path d="M17.3331 8.85479L5.43146 2.25722C4.68169 1.83269 4.01702 1.88083 3.60945 2.30615L13.8437 12.5L17.3331 8.85479Z" fill="#34A853"/>
                    </svg>
                    <div className="text-left">
                      <p className="text-dark-400 text-xs">Stáhnout na</p>
                      <p className="text-white font-semibold">Google Play</p>
                    </div>
                  </a>
                </div>
              </div>

              {/* QR Codes */}
              <div className="flex flex-col items-center gap-6">
                <p className="text-dark-400 text-sm">Nebo naskenuj QR kód:</p>
                <div className="flex gap-6">
                  {/* App Store QR */}
                  <div className="text-center">
                    <div className="w-32 h-32 bg-white rounded-xl p-2 mb-2">
                      <img
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(appStoreUrl)}`}
                        alt="App Store QR"
                        className="w-full h-full"
                      />
                    </div>
                    <p className="text-dark-400 text-xs">App Store</p>
                  </div>

                  {/* Google Play QR */}
                  <div className="text-center">
                    <div className="w-32 h-32 bg-white rounded-xl p-2 mb-2">
                      <img
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(googlePlayUrl)}`}
                        alt="Google Play QR"
                        className="w-full h-full"
                      />
                    </div>
                    <p className="text-dark-400 text-xs">Google Play</p>
                  </div>
                </div>
              </div>
            </div>

            {/* App Screenshots */}
            <div className="mt-10">
              <div className="flex justify-center gap-4">
                {[1, 2, 3, 4, 5].map((num) => (
                  <div
                    key={num}
                    className="w-40 rounded-2xl overflow-hidden shadow-xl border border-dark-700"
                  >
                    <img
                      src={`/images/appscreens/image${num}.png`}
                      alt={`Screenshot aplikace ${num}`}
                      className="w-full h-auto"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center">
          <div className="glass rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-white mb-4">Připraven začít?</h2>
            <p className="text-dark-400 mb-6">
              Zaregistruj se zdarma a začni se připravovat na přijímačky ještě dnes.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-8 py-3 rounded-xl gradient-bg text-white font-semibold hover:opacity-90 transition-opacity"
            >
              Vytvořit účet zdarma
              <ArrowRight size={20} />
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default About;
